import { type VariantProps, cva } from 'class-variance-authority';
import { NavLink } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../../Icon/Icon';
import { type IconName } from '../../Icon/icons';
import { Text } from '../../Text/Text';

const navItem = cva(
  'flex gap-xs min-w-full px-l group items-center focus:ring-3 hover:bg-surface-strong',
);

export type NavItemProps = VariantProps<typeof navItem> & {
  text: string;
  iconName: IconName;
  to?: string;
  collapsed?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
};

export const NavItem = ({
  text,
  iconName,
  to,
  onClick,
  ariaLabel,
  collapsed = false,
}: NavItemProps) => {
  const sharedProps = {
    role: 'menuitem',
    'aria-label': ariaLabel,
  };
  const itemBody = (
    <>
      <Icon name={iconName} size="large" />
      {!collapsed && (
        <Text size="bodyS" color="contentSubdued">
          {text}
        </Text>
      )}
    </>
  );

  return (
    <li role="none" className="list-none">
      {to ? (
        <NavLink
          to={to}
          className={({ isActive }) =>
            twMerge(
              navItem(),
              isActive && 'bg-accent-subtle hover:bg-accent-subtle',
            )
          }
          {...sharedProps}
        >
          {itemBody}
        </NavLink>
      ) : onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={navItem()}
          {...sharedProps}
        >
          {itemBody}
        </button>
      ) : null}
    </li>
  );
};
